<template>
  <v-container fluid class="pa-4 pa-sm-6">
    <!-- Шапка панели управления -->
    <PageTitle title="Управление темами Q&A" />
         <v-alert class=" mb-4" text="Модерация вопросов студентов. Ответы, переведенные в статус «Утвержден», автоматически индексируются ИИ-поиском (RAG)." type="info" variant="tonal"></v-alert>


    <v-row>
      <!-- ЛЕВАЯ КОЛОНКА: Список входящих вопросов -->
      <v-col cols="12" md="5" lg="4">
        <v-card class="rounded-lg fill-height d-flex flex-column" outlined elevation="1">
          <v-card-title class="pa-2 border-b">
                      <!-- Переключатель фильтра статусов -->
                      <v-btn-toggle
                        v-model="currentStatusFilter"
                        mandatory
                        color="secondary"
                        density="compact"
                        block
                        class="rounded-lg"
                        @update:model-value="fetchQuestionsByStatus"
                      >
                        <v-btn color="deep-orange-darken-1 " value="PENDING" @click="selectedQuestion = null" class="text-caption font-weight-bold flex-grow-1">
                          Входящие
                          <v-badge
                            :content="pendingCount"
                            :model-value="pendingCount > 0"
                            color="warning"
                            inline
                            class="ml-1"
                          ></v-badge>
                        </v-btn>
                        <v-btn color="deep-orange-darken-1"  @click="selectedQuestion  = null" value="ANSWERED" class="text-caption font-weight-bold flex-grow-1">
                            Черновик
                        </v-btn>
                        <v-btn color="deep-orange-darken-1"  @click="selectedQuestion  = null" value="APPROVED" class="text-caption font-weight-bold flex-grow-1">
                          Утвержденные
                        </v-btn>
                      </v-btn-toggle>
                    </v-card-title>

          <!-- Индикатор загрузки -->
          <v-progress-linear v-if="isLoading" indeterminate color="secondary"></v-progress-linear>

          <!-- Список карточек -->
           <!-- Список карточек по выбранному фильтру -->
                    <v-list class="pa-2 overflow-y-auto" style="max-height: 620px;" v-if="questionsList.length > 0">
                      <v-card
                        v-for="item in questionsList"
                        :key="item.id"
                        :color="selectedQuestion?.id === item.id ? 'blue-lighten-5' : ''"
                        class="mb-2 pa-3 rounded-lg cursor-pointer transition-all border"
                        variant="flat"
                        @click="selectQuestion(item)"
                      >
                        <div class="d-flex justify-space-between align-center mb-1">
                          <v-chip size="x-small" :color="item.status === 'APPROVED' ? 'success' : 'primary'" class="font-weight-bold">
                            {{ item.qaTopic?.topicTitle }}
                          </v-chip>
                          <span class="text-caption text-grey">{{ formatDate(item.createdAt) }}</span>
                        </div>
                        <div class="text-body-2 font-weight-bold text-truncate-2 text-grey-darken-4">
                          «{{ item.questionBody }}»
                        </div>
                      </v-card>
                    </v-list>

          <!-- Пустое состояние -->
          <v-card-text v-else class="text-center py-12 text-grey">
            <v-icon size="large" class="mb-2">mdi-check-all</v-icon>
            <div>Новых вопросов пока нет</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- ПРАВАЯ КОЛОНКА: Рабочая область ответа -->
      <v-col cols="12" md="7" lg="8">
        <v-card v-if="selectedQuestion" class="pa-4 rounded-lg fill-height" outlined elevation="1">
          <!-- Карточка самого вопроса -->
          <div class="bg-grey-lighten-4 pa-4 rounded-lg mb-4 position-relative border-l-md border-primary">
            <div class="text-caption text-deep-orange-darken-1 font-weight-bold mb-1">ВОПРОС СТУДЕНТА:</div>
            <div class="text-body-1 text-grey-darken-4 whitespace-pre-line font-weight-medium">
              {{ selectedQuestion.questionBody }}
            </div>
            <div class="text-caption text-grey-darken-1 mt-3 d-flex align-center gap-2">
              <span><strong>Автор:</strong> {{ selectedQuestion.studentName || 'Не указан' }}</span>
              <v-icon size="x-small" class="mx-1">mdi-circle-small</v-icon>
              <span><strong>Тема:</strong> {{ selectedQuestion.qaTopic }}</span>
              <span><strong>Статус:</strong> <v-chip :color="getStatusColor(selectedQuestion.status)" size="x-small" class="text-uppercase font-weight-bold">
                                                              {{ getStatusText(selectedQuestion.status) }}
                                                            </v-chip></span>
            </div>
          </div>

          <!-- Форма ответа эксперта -->
          <v-form ref="answerForm" v-model="isFormValid" @submit.prevent="submitAnswer(true)">
          <!-- Режим редактирования: Если вопрос еще НЕ утвержден в RAG -->
          <v-textarea
            v-if="selectedQuestion.status !== 'APPROVED'"
            v-model="answerBody"
            label="Экспертный ответ преподавателя"
            variant="outlined"
            color="primary"
            rows="8"
            auto-grow
            bg-color="white"
            :rules="[
              v => !!v || 'Ответ не может быть пустым',
              v => (v && v.length >= 20) || 'Дайте развернутый ответ (минимум 20 символов)'
            ]"
            placeholder="Напишите понятный и структурированный ответ для студента и базы знаний RAG..."
            class="mb-4 rounded-lg"
            prepend-inner-icon="mdi-text-box-check-outline"
          ></v-textarea>

          <!-- Режим просмотра: Красивая карточка для уже утвержденного ответа -->
          <v-card
            v-else
            variant="flat"
            color="green-lighten-5"
            class="mb-4 pa-4 rounded-lg border border-green-lighten-3 position-relative overflow-hidden"
          >
            <!-- Фоновый водяной знак ИИ/RAG для красоты -->
            <v-icon class="position-absolute-bg-icon" color="green-lighten-4" size="120">mdi-brain</v-icon>

            <div class="d-flex align-center justify-space-between mb-2 position-relative">
              <div class="text-caption text-success font-weight-black d-flex align-center">
                <v-icon start size="small" class="mr-1">mdi-check-decagram</v-icon>
                ОФИЦИАЛЬНЫЙ ОТВЕТ (УТВЕРЖДЕН В RAG)
              </div>

              <!-- Информационный чип -->
              <v-chip color="success" size="x-small" variant="flat" class="font-weight-bold text-uppercase">
                Индексирован
              </v-chip>
            </div>

            <!-- Сам текст ответа с сохранением форматирования абзацев -->
            <div class="text-body-1 text-grey-darken-4 font-weight-medium whitespace-pre-line position-relative pl-1">
              {{ answerBody }}
            </div>
          </v-card>

            <!-- Кнопки управления -->
            <v-row v-if="selectedQuestion.status!=='APPROVED'">
              <v-col cols="12" sm="6">
                <!-- Кнопка сохранения черновика (Без отправки в RAG) -->
                <v-btn
                  color="grey-darken-1"
                  variant="outlined"
                  size="large"
                  block
                  :loading="isSavingDraft"
                  :disabled="!isFormValid || isSubmitting"
                  @click="saveAnswer()"
                >
                  <v-icon start>mdi-file-document-edit-outline</v-icon>
                  Сохранить черновик
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6">
                <!-- Кнопка публикации в RAG -->
                <v-btn
                  color="success"
                  size="large"
                  block

                  :loading="isSubmitting"
                  :disabled="!isFormValid || isSavingDraft "
                  class="font-weight-bold"
                  @click="approveAnswer()"
                >
                  <v-icon start>mdi-brain</v-icon>
                  Утвердить
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card>

        <!-- Состояние, когда ни один вопрос не выбран -->
        <v-card v-else class="rounded-lg fill-height d-flex align-center justify-center py-12" outlined elevation="1" variant="flat">
          <div class="text-center text-grey-darken-1">
            <v-icon size="x-large" class="mb-2" color="grey-lighten-1">mdi-gesture-tap</v-icon>
            <div>Выберите вопрос из списка слева, чтобы приступить к ответу</div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script setup>
import { ref, onMounted } from 'vue'
import { customAxios } from "@/service/customAxios";
import PageTitle from "@/elements/PageTitle.vue";
import { usePopupStore } from "@/store/popup";
// Управление состояниями интерфейса
const isLoading = ref(false)
const isFormValid = ref(false)
const isSavingDraft = ref(false)
const isSubmitting = ref(false)
const answerForm = ref(null)

const currentStatusFilter = ref('PENDING')
const questionsList = ref([])
const pendingCount = ref(0)

// Данные
const pendingQuestions = ref([])
const selectedQuestion = ref(null)
const answerBody = ref('')

const { showToastPopup } = usePopupStore();

// Цветовая разметка статусов жизненного цикла QAPairStatus
const getStatusColor = (status) => {
  switch (status) {
    case 'PENDING': return 'warning'   // В очереди (оранжевый)
    case 'ANSWERED': return 'info'     // На проверке (черновик, синий)
    case 'APPROVED': return 'success'  // В базе RAG (зеленый)
    default: return 'grey'
  }
}

// Понятный перевод статусов для студента
const getStatusText = (status) => {
  switch (status) {
    case 'PENDING': return 'В очереди'
    case 'ANSWERED': return 'На проверке'
    case 'APPROVED': return 'Ответ готов'
    default: return status
  }
}

// Форматирование даты
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Выбор вопроса для ответа
const selectQuestion = (question) => {
  selectedQuestion.value = question
  answerBody.value = question.answerBody || '' // Подгружаем черновик, если он был
  if (answerForm.value) {
    answerForm.value.resetValidation()
  }
}

// Загрузка входящих вопросов (GET /api/v1/teacher/qa?status=PENDING)
const fetchPendingQuestions = async () => {
  isLoading.value = true
  try {
    const res = await customAxios.get('/v1/teacher/qa?status=PENDING')
    pendingQuestions.value = res.data
  } catch (e) {
     console.error('Ошибка при получении списка вопросов:', e)
  } finally {
    isLoading.value = false
  }
}

// Загрузка вопросов по выбранному статусу (PENDING или APPROVED)
const fetchQuestionsByStatus = async () => {
  isLoading.value = true
  try {
    const response = await customAxios.get('/v1/teacher/qa', {
      params: { status: currentStatusFilter.value }
    })
    questionsList.value = response.data

    // Если загрузили входящие, сохраняем их количество для бейджа
    if (currentStatusFilter.value === 'PENDING') {
      pendingCount.value = response.data.length
    }
  } catch (e) {
    // Ошибка перехватится глобальным интерцептором
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

// Отправка ответа (PUT /api/v1/teacher/qa/{id}/answer)
const saveAnswer = async () => {
  if (!isFormValid.value || !selectedQuestion.value) return

  try {
    const payload = {
      answerBody: answerBody.value,
    }
    const r = await customAxios.put(`/v1/teacher/qa/${selectedQuestion.value.id}/answer`, payload)
    if(r.status===202){
      showToastPopup ? showToastPopup('Вопрос успешно обновлен!') : alert('Вопрос успешно обновлен!');
      }

    await fetchQuestionsByStatus();
  } catch (e) {
    console.error('Ошибка при отправке ответа преподавателя:', e)
  } finally {
    isSubmitting.value = false
    isSavingDraft.value = false
  }
}
// Отправка ответа (PUT /api/v1/teacher/qa/{id}/answer)
const approveAnswer = async () => {
  if (!isFormValid.value || !selectedQuestion.value) return

  try {
    const payload = {
      answerBody: answerBody.value,
    }
    const r = await customAxios.put(`/v1/teacher/qa/${selectedQuestion.value.id}/answer/approve`, payload)
    if(r.status===202){
      showToastPopup ? showToastPopup('Вопрос успешно обновлен!') : alert('Вопрос успешно обновлен!');
      }
  selectedQuestion.value = null

    await fetchQuestionsByStatus();
  } catch (e) {
    console.error('Ошибка при отправке ответа преподавателя:', e)
  } finally {
    isSubmitting.value = false
    isSavingDraft.value = false
  }
}


onMounted(() => {
  fetchPendingQuestions()
})
</script>
<style scoped>
/* Плавный переход для эффекта наведения на карточки */
.transition-all {
  transition: all 0.2s ease-in-out;
}

.cursor-pointer {
  cursor: pointer;
}

/* Эффект легкого подъема карточки при наведении */
.cursor-pointer:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  border-color: var(--v-theme-primary) !important;
}

/* Обрезка длинного текста до 2 строк в списке слева */
.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Сохранение форматирования (переносов строк) в тексте вопроса */
.whitespace-pre-line {
  white-space: pre-line;
}

/* Кастомный гэп между элементами строки */
.gap-2 {
  gap: 8px;
}

/* Сохранение абзацев и переносов строк, которые ввел преподаватель */
.whitespace-pre-line {
  white-space: pre-line;
}

/* Красивое позиционирование фонового значка */
.position-relative {
  position: relative;
  z-index: 1;
}

.position-absolute-bg-icon {
  position: absolute !important;
  right: -20px;
  bottom: -30px;
  z-index: 0;
  opacity: 0.6;
  pointer-events: none;
}

</style>
